import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useModules } from "../hooks/useModules";
import { useAppDispatch } from "../hooks/useStore";
import { getCountries } from "../store/slices/companySlice";
import type { ModuleKey } from "../constants/modules";

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
  /**
   * The module this route belongs to. Absent means the route is always
   * available (profile, the driver home screen, the login redirects).
   *
   * Passing a key here is a UX guarantee, not a security one — the API refuses
   * disabled modules with a 404 regardless. What it buys is that nobody lands
   * on a screen that will only ever render errors.
   */
  module?: ModuleKey;
  /**
   * Routes that belong to more than one module and need only one of them.
   *
   * The container planning board is the case: it is the planning calendar and
   * it is a container screen, and a tenant who enabled either has bought it.
   * Mirrors `requireAnyModule` on the server, which is the authority — this only
   * keeps the two layers from disagreeing about where somebody may land.
   *
   * Ignored when `module` is also given; a route should say one thing.
   */
  anyOf?: ModuleKey[];
  redirectTo?: string;
}

/**
 * The single gate in front of every authenticated route.
 *
 * Three questions, in order, each with a different answer:
 *
 *   1. Signed in?      → no: to /login, remembering where they were going.
 *   2. Right role?     → no: to their own home screen.
 *   3. Module enabled? → no: to their own home screen, silently.
 *
 * Steps 2 and 3 both redirect rather than explain. An "access denied" or
 * "module disabled" screen would confirm to whoever typed the URL that the
 * route exists and that some other tenant has it — the backend answers 404 for
 * exactly this reason, and the two layers must not disagree about how much they
 * give away. A redirect is indistinguishable from a mistyped path.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles = [],
  module,
  anyOf,
  redirectTo = "/login",
}) => {
  const { isAuthenticated, user } = useAuth();
  const { has, homePath } = useModules();
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      dispatch(getCountries());
    }
  }, []);

  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to={homePath} replace />;
  }

  if (module && !has(module)) {
    return <Navigate to={homePath} replace />;
  }

  if (!module && anyOf?.length && !anyOf.some(has)) {
    return <Navigate to={homePath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
